import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiSave, FiSend } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { getManagerAssessment, saveManagerAssessment } from "../../services/assessmentApi";
import useAsync from "../../hooks/useAsync";
import useFormDraft from "../../hooks/useFormDraft";
import { Card, PageHeader, Loader, ErrorState, Button, FormField, Banner, StatusBadge, ConfirmDialog, EvidencePanel, DraftNotice } from "../../components/common";
import ScoreBreakdown from "../../components/performance/ScoreBreakdown";
import AssessmentSections from "../../components/performance/AssessmentSections";
import { MANAGER_SECTION_MAP } from "../../services/core";
import { directionLabel } from "../../components/performance/KraKpiTable";
import { KpiRecordedHint } from "../../components/performance/KpiMeasurements";
import { computePerformance } from "../../utils/scoreCalculator";
import { formatDateTime } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";
import { ASSESSMENT_STATUS } from "../../utils/constants";

const api = { getManagerAssessment, saveManagerAssessment };

const toMap = (list, fields) => Object.fromEntries(list.map((x) => [x.id, Object.fromEntries(fields.map((f) => [f, x[f] ?? ""]))]));

export default function ManagerAssessmentPage() {
  const { employeeId } = useParams();
  const { cycleId, cycle } = useCycle();
  const navigate = useNavigate();
  const { refreshCounters } = useOutletContext() || {};
  const { data, error, reload } = useAsync(() => api.getManagerAssessment(cycleId, employeeId), [cycleId, employeeId], { skip: !cycleId });

  const [form, setForm] = useState({ comments: "", strengths: "", improvementAreas: "", developmentPlan: "" });
  const [goalRows, setGoalRows] = useState({});
  const [kraRows, setKraRows] = useState({});
  const [kpiRows, setKpiRows] = useState({});
  const [saving, setSaving] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!data) return;
    const m = data.managerAssessment;
    const { status, submittedAt, ...texts } = m;
    setForm({ comments: "", strengths: "", improvementAreas: "", developmentPlan: "", ...texts });
    setGoalRows(Object.fromEntries(data.goals.map((g) => [g.id, { managerAchievement: g.managerAchievement ?? g.achievement ?? "", managerComments: g.managerComments || "" }])));
    setKraRows(toMap(data.kras.filter((kra) => kra.assessmentInputRequired), ["actual", "managerComments"]));
    setKpiRows(toMap(data.kpis, ["actual", "managerComments"]));
  }, [data]);

  // [NEW] Everything typed on this page (texts + per goal/KRA/KPI rows) is kept in this browser until saved,
  // so a power cut, a closed tab or an urgent meeting does not lose the work.
  const pageText = useMemo(() => ({ form, goalRows, kraRows, kpiRows }), [form, goalRows, kraRows, kpiRows]);
  const setPageText = useCallback((v) => { setForm(v.form); setGoalRows(v.goalRows || {}); setKraRows(v.kraRows || {}); setKpiRows(v.kpiRows || {}); }, []);
  const editable = Boolean(data) && data.managerAssessment.status !== ASSESSMENT_STATUS.SUBMITTED && data.stageOpen;
  const draft = useFormDraft(`manager-assessment:${cycleId}:${employeeId}`, pageText, setPageText, { active: editable });

  const preview = useMemo(() => {
    if (!data) return {};
    return computePerformance({
      goals: data.goals.map((g) => ({ ...g, managerAchievement: goalRows[g.id]?.managerAchievement })),
      kras: data.kras.map((kra) => (kra.assessmentInputRequired ? { ...kra, actual: kraRows[kra.id]?.actual } : kra)),
      kpis: data.kpis.map((k) => ({ ...k, actual: kpiRows[k.id]?.actual })),
      config: data.config,
    });
  }, [data, goalRows, kraRows, kpiRows]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const self = data.selfAssessment;
  const selfDone = self?.status === ASSESSMENT_STATUS.SUBMITTED;
  const submitted = data.managerAssessment.status === ASSESSMENT_STATUS.SUBMITTED;
  const readOnly = submitted || !data.stageOpen;
  // [UPDATED] Highest agreed % allowed: the policy's score cap, or 100% when the policy has no cap.
  const maxAgreed = data.config.scoreCap || 100;

  const update = (setter, id, field) => (e) => setter((rows) => ({ ...rows, [id]: { ...rows[id], [field]: e.target.value } }));

  const save = async (submit) => {
    setSaving(submit ? "submit" : "draft");
    try {
      await api.saveManagerAssessment(cycleId, employeeId, {
        ...form,
        goalUpdates: Object.entries(goalRows).map(([id, v]) => ({ id, ...v })),
        kraUpdates: Object.entries(kraRows).map(([id, v]) => ({ id, ...v })),
        kpiUpdates: Object.entries(kpiRows).map(([id, v]) => ({ id, ...v })),
      }, submit);
      draft.clear();
      toast.success(submit ? "Assessment sent to HR for finalization." : "Draft saved.");
      setConfirm(false);
      if (submit) {
        refreshCounters?.();
        navigate("/team/assessments");
      } else reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="page">
      <PageHeader
        backTo="/team/assessments"
        backLabel="Team assessments"
        title={`Assess ${data.employee.name}`}
        subtitle={`${data.employee.designationName}, ${cycle?.name}`}
        actions={<StatusBadge status={data.managerAssessment.status} />}
      />

      <DraftNotice draft={draft} />

      {!selfDone && (
        <Banner tone="warning" title="Self assessment not submitted yet">
          You can prepare a draft, but you can submit only after {data.employee.name} submits their self assessment.
        </Banner>
      )}
      {submitted && (
        <Banner tone="success" title="Sent to HR">
          Submitted on {formatDateTime(data.managerAssessment.submittedAt)}. Result status: {data.resultStatus}.
        </Banner>
      )}
      {!submitted && !data.stageOpen && (
        <Banner tone="neutral" title="Manager review is not open">Your entries are read-only until HR opens this stage.</Banner>
      )}

      <div className="assessment-layout">
        <div className="stack">
          <Card title={`${data.employee.name}'s self assessment`} subtitle={selfDone ? `Submitted ${formatDateTime(self.submittedAt)}` : "Not submitted"}>
            {self ? (
              <div className="feedback-grid">
                <div><p className="detail-label">Key achievements</p><p className="detail-text">{self.keyAchievements || "—"}</p></div>
                <div><p className="detail-label">Strengths</p><p className="detail-text">{self.strengths || "—"}</p></div>
                <div><p className="detail-label">Challenges</p><p className="detail-text">{self.challenges || "—"}</p></div>
                <div><p className="detail-label">Development areas</p><p className="detail-text">{self.developmentAreas || "—"}</p></div>
              </div>
            ) : (
              <p className="muted">Nothing entered yet.</p>
            )}
          </Card>

          <Card title="Goals" subtitle="The employee's rating is shown. Set the achievement you agree with.">
            <div className="line-table">
              {data.goals.map((g) => (
                <div key={g.id} className="line-row">
                  <div className="line-info">
                    <strong>{g.title}</strong>
                    <small>Goal {g.weightage}%. Target {g.target} {g.unit}.</small>
                    <small className="self-note">Self-rated {g.achievement ?? "—"}{g.achievement != null ? "%" : ""}{g.employeeComments ? `: ${g.employeeComments}` : ""}</small>
                  </div>
                  <FormField label="Agreed %" className="line-num" hint={`Up to ${maxAgreed}%`}>
                    <input type="number" min="0" max={maxAgreed} value={goalRows[g.id]?.managerAchievement ?? ""} onChange={update(setGoalRows, g.id, "managerAchievement")} disabled={readOnly} />
                  </FormField>
                  <FormField label="Your comment" className="line-comment">
                    <input value={goalRows[g.id]?.managerComments ?? ""} onChange={update(setGoalRows, g.id, "managerComments")} disabled={readOnly} />
                  </FormField>
                </div>
              ))}
              {!data.goals.length && <p className="muted">No approved goals.</p>}
            </div>
          </Card>

          <Card title="KRA and KPI results" subtitle="Review KPI actuals. Each KRA score is calculated from its child KPIs.">
            <div className="line-table">
              {data.kras.map((k) => {
                const derived = preview.kraBreakdown?.find((row) => String(row.kraItemId) === String(k.id));
                return (
                  <div key={k.id} className="line-row">
                    <div className="line-info">
                      <strong><span className="tag">KRA</span> {k.name}</strong>
                      <small>{k.assessmentInputRequired
                        ? `Target ${k.target}. Weight ${k.weightage}%.`
                        : `${k.weightage}% KRA weight. Calculated from ${k.kpis?.length || 0} child KPI${k.kpis?.length === 1 ? "" : "s"}.`}</small>
                    </div>
                    {k.assessmentInputRequired ? (
                      <>
                        <FormField label="Actual" className="line-num">
                          <input type="number" step="any" min="0" value={kraRows[k.id]?.actual ?? ""} onChange={update(setKraRows, k.id, "actual")} disabled={readOnly} />
                        </FormField>
                        <FormField label="Comment" className="line-comment">
                          <input value={kraRows[k.id]?.managerComments ?? ""} onChange={update(setKraRows, k.id, "managerComments")} disabled={readOnly} />
                        </FormField>
                      </>
                    ) : (
                      <div className="line-num">
                        <span className="detail-label">Derived score</span>
                        <strong>{derived?.score == null ? "—" : `${derived.score}%`}</strong>
                      </div>
                    )}
                  </div>
                );
              })}
              {data.kpis.map((k) => (
                <div key={k.id} className="line-row">
                  <div className="line-info">
                    <strong><span className="tag tag-info">KPI</span> {k.name}</strong>
                    <small>{directionLabel(k.direction)}. Target {k.target} {k.unit}.</small>
                    <KpiRecordedHint kpi={k} disabled={readOnly} onUse={(v) => setKpiRows((rows) => ({ ...rows, [k.id]: { ...rows[k.id], actual: String(v) } }))} />
                  </div>
                  <FormField label="Actual" className="line-num">
                    <input type="number" step="any" min="0" value={kpiRows[k.id]?.actual ?? ""} onChange={update(setKpiRows, k.id, "actual")} disabled={readOnly} />
                  </FormField>
                  <FormField label="Comment" className="line-comment">
                    <input value={kpiRows[k.id]?.managerComments ?? ""} onChange={update(setKpiRows, k.id, "managerComments")} disabled={readOnly} />
                  </FormField>
                </div>
              ))}
              {!data.kras.length && !data.kpis.length && <p className="muted">No KRAs or KPIs assigned.</p>}
            </div>
          </Card>

          <EvidencePanel
            owner="performance"
            ownerId={data.recordId}
            canUpload={!readOnly && selfDone}
            context="MANAGER_ASSESSMENT"
            title="Evidence"
            subtitle="The employee's files (after they submit) and your own manager evidence."
          />

          <Card title="Your assessment" subtitle="The sections HR chose for this cycle. All of them are required to submit.">
            <AssessmentSections
              allowed={data.sections}
              map={MANAGER_SECTION_MAP}
              labels={{
                comments: { label: "Overall comments" },
                strengths: { label: "Strengths" },
                improvementAreas: { label: "Areas to improve" },
                developmentPlan: { label: "Development plan", hint: "Visible to the employee after HR finalizes." },
              }}
              form={form}
              setForm={setForm}
              readOnly={readOnly}
            />
          </Card>
        </div>

        <aside className="assessment-side">
          <Card>
            <ScoreBreakdown performance={preview} weights={data.config.weights} restricted={data.config.restricted} scoreCap={data.config.scoreCap} title="Calculated score" provisional />
            {!readOnly && (
              <div className="side-actions">
                <Button variant="secondary" icon={FiSave} block loading={saving === "draft"} disabled={Boolean(saving)} onClick={() => save(false)}>Save draft</Button>
                <Button icon={FiSend} block disabled={Boolean(saving) || !selfDone} onClick={() => setConfirm(true)}>Submit to HR</Button>
              </div>
            )}
          </Card>
        </aside>
      </div>

      <ConfirmDialog
        open={confirm}
        title="Submit this assessment to HR?"
        message={`HR will review and finalize ${data.employee.name}'s rating. You won't be able to edit after submitting.`}
        confirmLabel="Submit to HR"
        loading={saving === "submit"}
        onConfirm={() => save(true)}
        onClose={() => setConfirm(false)}
      />
    </div>
  );
}